import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('burialPlot');
export interface BurialPlotData {
  id?: string;
  cemeteryName?: string;
  cemeteryAddress?: string;
  cemeteryPhone?: string;
  cemeteryEmail?: string;
  cemeteryWebsite?: string;
  lotNumber?: string;
  deedLocation?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  notes?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBurialPlotRequest {
  cemeteryName?: string;
  cemeteryAddress?: string;
  cemeteryPhone?: string;
  cemeteryEmail?: string;
  cemeteryWebsite?: string;
  lotNumber?: string;
  deedLocation?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  notes?: string;
}

export interface UpdateBurialPlotRequest extends CreateBurialPlotRequest {}

export const burialPlotService = {
  createBurialPlot: async (data: CreateBurialPlotRequest): Promise<BurialPlotData> => {
    try {
      logger.debug('[Service] Creating burial plot with data:', data);
      const response = await http.post('/api/burial-plot-info', data);
      logger.debug('[Service] Create response:', response.data);
      return response.data.data;
    } catch (error: any) {
      logger.error('[Service] Error creating burial plot:', error);
      throw error;
    }
  },

  updateBurialPlot: async (id: string, data: UpdateBurialPlotRequest): Promise<BurialPlotData> => {
    try {
      logger.debug('[Service] Updating burial plot with ID:', id, 'data:', data);
      const response = await http.patch(`/api/burial-plot-info/${id}`, data);
      logger.debug('[Service] Update response:', response.data);
      return response.data.data;
    } catch (error: any) {
      logger.error('[Service] Error updating burial plot:', error);
      throw error;
    }
  },

  getAllBurialPlots: async (): Promise<BurialPlotData[]> => {
    try {
      logger.debug('[Service] Getting all burial plot info');
      const response = await http.get('/api/burial-plot-info');
      logger.debug('[Service] Get all response:', response.data);
      return response.data.data || [];
    } catch (error: any) {
      logger.error('[Service] Error getting burial plot info:', error);
      throw error;
    }
  },

  getBurialPlotById: async (id: string): Promise<BurialPlotData> => {
    try {
      logger.debug('[Service] Getting burial plot by ID:', id);
      const response = await http.get(`/api/burial-plot-info/${id}`);
      logger.debug('[Service] Get by ID response:', response.data);
      return response.data.data;
    } catch (error: any) {
      logger.error('[Service] Error getting burial plot by ID:', error);
      throw error;
    }
  },

  deleteBurialPlot: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Deleting burial plot with ID:', id);
      await http.delete(`/api/burial-plot-info/${id}`);
      logger.debug('[Service] Delete completed');
    } catch (error: any) {
      logger.error('[Service] Error deleting burial plot:', error);
      throw error;
    }
  },

  lockBurialPlot: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Locking burial plot with ID:', id);
      await http.post(`/api/burial-plot-info/${id}/lock`);
      logger.debug('[Service] Lock completed');
    } catch (error: any) {
      logger.error('[Service] Error locking burial plot:', error);
      throw error;
    }
  },

  unlockBurialPlot: async (id: string, granteeEmails: string[]): Promise<void> => {
    try {
      logger.debug('[Service] Unlocking burial plot with ID:', id, 'for emails:', granteeEmails);
      await http.post(`/api/burial-plot-info/${id}/unlock`, { granteeEmails });
      logger.debug('[Service] Unlock completed');
    } catch (error: any) {
      logger.error('[Service] Error unlocking burial plot:', error);
      throw error;
    }
  },

  getBurialPlotAccessGrants: async (id: string): Promise<any[]> => {
    try {
      logger.debug('[Service] Getting access grants for burial plot with ID:', id);
      const response = await http.get(`/api/burial-plot-info/${id}/access-grants`);
      logger.debug('[Service] Access grants response:', response.data);
      return response.data.data || [];
    } catch (error: any) {
      logger.error('[Service] Error getting burial plot access grants:', error);
      throw error;
    }
  },
};